import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, Calendar, DollarSign, ArrowLeft, Trash2, Eye, MapPin, CheckCircle, Bell } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomerWishlist() {
  const navigate = useNavigate();
  const [wishlistEvents, setWishlistEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [eventMembershipStatus, setEventMembershipStatus] = useState(null);
  const [joiningEvent, setJoiningEvent] = useState(false);

  useEffect(() => {
    fetchWishlist();
  }, []);

  const fetchWishlist = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/wishlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlistEvents(response.data);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      toast.error('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWishlist = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/wishlist/remove/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Removed from wishlist');
      fetchWishlist();
    } catch (error) {
      toast.error('Failed to remove from wishlist');
    }
  };

  const handleViewEventDetails = async (event) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/events/${event.event_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEvent(response.data);
      
      // Check membership status
      await checkEventMembership(event.event_id);
      
      setShowEventDialog(true);
    } catch (error) {
      console.error('Error fetching event details:', error);
      toast.error('Failed to load event details');
    }
  };

  const checkEventMembership = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/events/${eventId}/membership-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEventMembershipStatus(response.data.status);
    } catch (error) {
      setEventMembershipStatus(null);
    }
  };

  const handleJoinEvent = async (eventId) => {
    setJoiningEvent(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/events/${eventId}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Successfully joined the event!');
      await checkEventMembership(eventId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join event');
    } finally {
      setJoiningEvent(false);
    }
  };

  const handleViewEvent = (eventId) => {
    navigate(`/customer/events`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream pt-6 pb-12 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="font-outfit text-gray-600">Loading wishlist...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pt-6 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          onClick={() => navigate('/customer/events')}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Events
        </Button>

        <div className="mb-8">
          <h1 className="font-playfair font-bold text-4xl text-primary mb-2 flex items-center gap-3">
            <Heart className="w-10 h-10 text-red-500 fill-red-500" />
            My Wishlist
          </h1>
          <p className="font-outfit text-gray-600">Events you want to join later</p>
        </div>

        {wishlistEvents.length === 0 ? (
          <Card className="p-12 text-center">
            <Heart className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="font-playfair text-2xl font-bold text-gray-700 mb-2">No Events in Wishlist</h3>
            <p className="font-outfit text-gray-600 mb-6">
              Start adding events you're interested in to your wishlist!
            </p>
            <Button onClick={() => navigate('/customer/events')} className="bg-primary hover:bg-primary/90">
              Browse Events
            </Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {wishlistEvents.map((event) => (
              <Card key={event.event_id} className="p-6 hover:shadow-lg transition-shadow relative">
                <Button
                  onClick={() => handleRemoveFromWishlist(event.event_id)}
                  variant="ghost"
                  size="sm"
                  className="absolute top-4 right-4 text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-5 h-5" />
                </Button>

                <div className="mb-4">
                  <h3 className="font-playfair font-bold text-2xl text-primary mb-2">
                    {event.event_name}
                  </h3>
                  <p className="font-outfit text-sm text-gray-600 line-clamp-2">
                    {event.event_description}
                  </p>
                </div>

                <div className="space-y-2 text-sm font-outfit mb-4">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(event.start_date).toLocaleDateString()} - {new Date(event.end_date).toLocaleDateString()}</span>
                  </div>
                  {/* Only show coupon value if not hidden */}
                  {!event.coupon_value_hidden && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <span>Incentive Points: {event.coupon_value}</span>
                    </div>
                  )}
                  <p className="text-gray-600">
                    <strong>Business:</strong> {event.business_name}
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleViewEventDetails(event)}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Details
                  </Button>
                  <Button
                    onClick={() => handleViewEvent(event.event_id)}
                    variant="outline"
                    className="flex-1"
                  >
                    Go to Events
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Event Details Dialog */}
        <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
          <DialogContent className="max-w-2xl rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-playfair text-2xl text-primary">Event Details</DialogTitle>
            </DialogHeader>
            {selectedEvent && (
              <div className="space-y-4">
                <div>
                  <h3 className="font-playfair font-bold text-xl text-primary mb-2">{selectedEvent.event_name}</h3>
                  <p className="text-sm text-gray-600 mb-1">By {selectedEvent.business_name}</p>
                  <p className="text-gray-700">{selectedEvent.event_description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 py-4 border-y">
                  <div>
                    <div className="flex items-center text-gray-600 mb-2">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span className="text-sm font-semibold">Start Date</span>
                    </div>
                    <p className="text-sm">{new Date(selectedEvent.start_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <div className="flex items-center text-gray-600 mb-2">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span className="text-sm font-semibold">End Date</span>
                    </div>
                    <p className="text-sm">{new Date(selectedEvent.end_date).toLocaleDateString()}</p>
                  </div>
                  {/* Only show coupon value if not hidden */}
                  {!selectedEvent.coupon_value_hidden && (
                    <div>
                      <div className="flex items-center text-gray-600 mb-2">
                        <span className="text-sm font-semibold">Incentive Points</span>
                      </div>
                      <p className="text-sm font-bold text-green-600">{selectedEvent.coupon_value}</p>
                    </div>
                  )}
                  <div>
                    <div className="flex items-center text-gray-600 mb-2">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span className="text-sm font-semibold">Status</span>
                    </div>
                    <p className="text-sm capitalize">{selectedEvent.status}</p>
                  </div>
                </div>

                {selectedEvent.prizes && selectedEvent.prizes.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Prizes:</h4>
                    <ul className="space-y-2">
                      {selectedEvent.prizes.map((prize, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm">
                          <span className="text-amber-600 font-bold mt-0.5">{idx + 1}.</span>
                          <div className="flex-1">
                            <div className="flex flex-wrap items-baseline gap-2">
                              <span className="font-semibold text-gray-800">{prize.prize_name || `Prize ${idx + 1}`}</span>
                              <span className="text-amber-600 font-bold text-base">${prize.prize_amount}</span>
                            </div>
                            {prize.prize_description && (
                              <p className="text-gray-600 text-xs mt-1">{prize.prize_description}</p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex flex-col gap-2 pt-4">
                  {eventMembershipStatus === 'approved' ? (
                    <div className="flex items-center justify-center gap-2 text-green-600 font-semibold py-2 bg-green-50 rounded-lg">
                      <CheckCircle className="w-5 h-5" />
                      Already Joined
                    </div>
                  ) : eventMembershipStatus === 'pending' ? (
                    <div className="flex items-center justify-center gap-2 text-yellow-600 font-semibold py-2 bg-yellow-50 rounded-lg">
                      <Bell className="w-5 h-5" />
                      Join Request Pending
                    </div>
                  ) : (
                    <Button
                      onClick={() => handleJoinEvent(selectedEvent.event_id)}
                      disabled={joiningEvent}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      {joiningEvent ? 'Joining...' : 'Join Event'}
                    </Button>
                  )}
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        setShowEventDialog(false);
                        navigate('/customer/events');
                      }}
                      variant="outline"
                      className="flex-1"
                    >
                      Go to Events Page
                    </Button>
                    <Button
                      onClick={() => setShowEventDialog(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
